import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileText, Trash2, Upload, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import {
  useImportFile,
  useProject,
  type ImportFileInput,
  type ImportSummary,
} from '@/lib/api';

type ImportFormat = 'po' | 'pot' | 'json';

interface PlannedImport {
  id: string;
  file: File;
  /**
   * Language extracted from the filename (e.g. `pt-BR.po` → `pt-BR`). Editable
   * by the user before upload. Empty means "import source only", which the API
   * understands as "create/update keys without touching translations".
   */
  lang: string;
  format: ImportFormat;
  namespace: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  summary?: ImportSummary;
  error?: string;
}

const FORMAT_FROM_EXT: Record<string, ImportFormat> = {
  po: 'po',
  pot: 'pot',
  json: 'json',
};

/**
 * Guess the BCP-47 language code from a filename like `pt-BR.po`, `es.json`,
 * `messages_de.po`. Returns '' if we can't pull a confident hint — the user
 * still gets a chance to fix it in the staging table before upload.
 */
function guessLang(name: string): string {
  const base = name.replace(/\.(po|pot|json)$/i, '');
  // pt-BR.po, en.po, zh-Hans.po
  const direct = base.match(/^([a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*)$/);
  if (direct) return direct[1];
  // messages_de.po, app.pt-BR.po
  const tail = base.match(/[._-]([a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*)$/);
  if (tail) return tail[1];
  return '';
}

function guessFormat(name: string): ImportFormat {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return FORMAT_FROM_EXT[ext] ?? 'po';
}

let idCounter = 0;

export function ProjectImportTab() {
  const { projId = '' } = useParams<{ projId: string }>();
  const project = useProject(projId);
  const importFile = useImportFile();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [queue, setQueue] = useState<PlannedImport[]>([]);
  const [dragging, setDragging] = useState(false);

  function addFiles(files: FileList | File[]) {
    const planned: PlannedImport[] = Array.from(files)
      .filter((f) => /\.(po|pot|json)$/i.test(f.name))
      .map((f) => ({
        id: `f-${++idCounter}`,
        file: f,
        lang: guessLang(f.name),
        format: guessFormat(f.name),
        namespace: '',
        status: 'pending',
      }));
    setQueue((cur) => [...cur, ...planned]);
  }

  function updatePlan(id: string, patch: Partial<PlannedImport>) {
    setQueue((cur) => cur.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  function removePlan(id: string) {
    setQueue((cur) => cur.filter((p) => p.id !== id));
  }

  async function uploadAll() {
    const ready = queue.filter((p) => p.status === 'pending' || p.status === 'error');
    for (const plan of ready) {
      updatePlan(plan.id, { status: 'uploading', error: undefined });
      try {
        const input: ImportFileInput = {
          projectID: projId,
          file: plan.file,
          format: plan.format,
          filename: plan.file.name,
          lang: plan.lang || undefined,
          namespace: plan.namespace || undefined,
        };
        const summary = await importFile.mutateAsync(input);
        updatePlan(plan.id, { status: 'done', summary });
      } catch (err) {
        const message = (err as Error).message ?? 'Upload failed';
        updatePlan(plan.id, { status: 'error', error: message });
      }
    }
    const doneCount = ready.length - queue.filter((p) => p.status === 'error').length;
    if (doneCount > 0) toast.show(`Imported ${doneCount} file${doneCount === 1 ? '' : 's'}`, 'success');
  }

  function clearDone() {
    setQueue((cur) => cur.filter((p) => p.status !== 'done'));
  }

  const ready = queue.filter((p) => p.status === 'pending' || p.status === 'error').length;
  const targets = project.data?.targetLanguageCodes ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Upload className="size-4 text-primary" aria-hidden />
          Bulk import
        </CardTitle>
        <CardDescription>
          Drop one or more <code>.po</code>, <code>.pot</code>, or <code>.json</code> files.
          Language is auto-detected from each filename; review before upload.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
          }}
          className={
            dragging
              ? 'rounded-lg border-2 border-dashed border-primary bg-primary/5 px-6 py-10 text-center transition-colors'
              : 'rounded-lg border-2 border-dashed border-border bg-muted/30 px-6 py-10 text-center transition-colors hover:border-primary/40'
          }
        >
          <Upload className="mx-auto size-6 text-primary" aria-hidden />
          <p className="mt-2 text-sm font-medium text-foreground">
            Drop files here, or
            <button
              type="button"
              className="ml-1 text-primary underline-offset-4 hover:underline"
              onClick={() => inputRef.current?.click()}
            >
              browse
            </button>
            .
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Supported: <code>*.po</code>, <code>*.pot</code>, <code>*.json</code>. Multi-file
            allowed.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".po,.pot,.json,application/octet-stream"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {queue.length === 0 ? null : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                    <th className="px-3 py-2 text-left font-medium">Language</th>
                    <th className="px-3 py-2 text-left font-medium">Format</th>
                    <th className="px-3 py-2 text-left font-medium">Namespace</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="w-10 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {queue.map((p) => (
                    <tr key={p.id} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center gap-2">
                          <FileText className="size-3.5 text-muted-foreground" aria-hidden />
                          <span className="text-sm">{p.file.name}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {(p.file.size / 1024).toFixed(1)} KB
                        </p>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          aria-label={`Language for ${p.file.name}`}
                          value={p.lang}
                          onChange={(e) => updatePlan(p.id, { lang: e.target.value })}
                          disabled={p.status !== 'pending' && p.status !== 'error'}
                          className="h-8 rounded-md border border-input bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">(source only)</option>
                          {project.data?.sourceLanguageCode && (
                            <option value={project.data.sourceLanguageCode}>
                              {project.data.sourceLanguageCode} (source)
                            </option>
                          )}
                          {targets.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                          {p.lang && !targets.includes(p.lang) && p.lang !== project.data?.sourceLanguageCode && (
                            <option value={p.lang}>{p.lang}</option>
                          )}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          aria-label={`Format for ${p.file.name}`}
                          value={p.format}
                          onChange={(e) =>
                            updatePlan(p.id, { format: e.target.value as ImportFormat })
                          }
                          disabled={p.status !== 'pending' && p.status !== 'error'}
                          className="h-8 rounded-md border border-input bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="po">po</option>
                          <option value="pot">pot</option>
                          <option value="json">json</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          aria-label={`Namespace for ${p.file.name}`}
                          value={p.namespace}
                          onChange={(e) => updatePlan(p.id, { namespace: e.target.value })}
                          disabled={p.status !== 'pending' && p.status !== 'error'}
                          placeholder="(default)"
                          className="h-8 w-32 rounded-md border border-input bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </td>
                      <td className="px-3 py-2">
                        {p.status === 'pending' && (
                          <Badge tone="outline">queued</Badge>
                        )}
                        {p.status === 'uploading' && <Spinner label="uploading…" />}
                        {p.status === 'done' && p.summary && (
                          <div className="inline-flex items-center gap-2">
                            <Badge tone="positive">
                              <CheckCircle2 className="size-3" aria-hidden /> ok
                            </Badge>
                            <span className="text-[11px] text-muted-foreground">
                              +{p.summary.keysCreated ?? 0}k · ~{p.summary.keysUpdated ?? 0}k ·{' '}
                              {p.summary.translationsWrote ?? 0}t
                            </span>
                          </div>
                        )}
                        {p.status === 'error' && (
                          <div className="inline-flex items-start gap-2">
                            <Badge tone="negative">
                              <XCircle className="size-3" aria-hidden /> failed
                            </Badge>
                            {p.error && (
                              <span className="text-[11px] text-negative">{p.error}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${p.file.name}`}
                          onClick={() => removePlan(p.id)}
                          disabled={p.status === 'uploading'}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={clearDone}
                disabled={queue.every((p) => p.status !== 'done')}
              >
                Clear done
              </Button>
              <Button
                onClick={() => void uploadAll()}
                disabled={ready === 0 || importFile.isPending}
              >
                {importFile.isPending
                  ? 'Uploading…'
                  : ready === 1
                    ? 'Upload 1 file'
                    : `Upload ${ready} files`}
              </Button>
            </div>

            <div className="space-y-2">
              {queue
                .filter((p) => p.summary?.placeholderErrors && p.summary.placeholderErrors.length > 0)
                .map((p) => (
                  <div
                    key={`err-${p.id}`}
                    className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs"
                  >
                    <p className="font-medium text-warning">{p.file.name}: placeholder mismatches</p>
                    <Label htmlFor="ph-err" className="sr-only">
                      Placeholder mismatches
                    </Label>
                    <ul id="ph-err" className="mt-1 list-disc pl-5 text-warning">
                      {(p.summary?.placeholderErrors ?? []).slice(0, 10).map((e) => (
                        <li key={e}>
                          <code>{e}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
